import React from "react";
import { useField, useFormikContext } from "formik";
import ErrorField from "../errorField";
// import FormLabel from "../formLabel";
import FormGroup from "../formGroup";
import TextArea from "../../core/textarea";
import { TextAreaFieldConfig } from "../types.d";
import { FieldResetWhenRemoved, getValueForReadOnlyFieldForStaticForm } from "shared/form/util";
import FormOverwriteField from "../overwriteField";
import { isEmpty } from "../../../utils";
import { useFormContextData } from "shared/form/context";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";
import FieldKiPehchan from "../fieldKiPehchan";
import AITextEnhancer from "shared/components/aiTextEnhancer";
import {
  MAX_DEFAULT_LENGTH_FOR_AI_TEXT_ENHANCEMENT,
  MIN_DEFAULT_LENGTH_TO_ENABLE_AI_TEXTFIELD_ASSIST,
  MIN_DEFAULT_TEXTAREA_LENGTH
} from "shared/utils/constants";

function FormTextAreaField({ fullWidth = true, ...props }: TextAreaFieldConfig) {
  const formContextData = useFormContextData();
  const { setFieldTouched, setFieldValue, validateForm, validateOnChange } = useFormikContext();
  const [{ ...field }, meta] = useField({
    name: props.name,
    validate: props.validate
  });

  const changeHandler = React.useCallback(
    async e => {
      await setFieldValue(props.name, e.target.value, false);
      if (!meta.touched) {
        await setFieldTouched(props.name, true, false);
      }
      validateOnChange && (await validateForm());
    },
    [setFieldValue, setFieldTouched, validateForm, props.name, meta.touched, validateOnChange]
  );

  const aiTextEnhancementUpdateHandler = React.useCallback(
    (updatedText: string) => {
      changeHandler({ target: { value: updatedText } });
    },
    [changeHandler]
  );

  const canDisplayAITextEnhancement = React.useMemo(() => {
    return Boolean(!props.disabled && props.isAITextAssistEnabled && props.registrationId);
  }, [props.disabled, props.isAITextAssistEnabled, props.registrationId]);

  return (
    <React.Fragment>
      <FormGroup className={props.className} fullWidth={fullWidth} indented={props.indented}>
        <FieldKiPehchan
          label={props.label}
          required={props.required}
          additionalLabel={props.additionalLabel}
          htmlFor={props.name}
          infoLabel={props.infoLabel}
          iconUrl={props.iconUrl}
          right={
            canDisplayAITextEnhancement ? (
              <AITextEnhancer
                minLength={props.minLength ?? MIN_DEFAULT_TEXTAREA_LENGTH}
                minLengthToEnableAIAssist={MIN_DEFAULT_LENGTH_TO_ENABLE_AI_TEXTFIELD_ASSIST}
                maxLength={props.maxCount ?? MAX_DEFAULT_LENGTH_FOR_AI_TEXT_ENHANCEMENT}
                value={meta.value}
                renderType={FieldRenderType.MULTI_LINE_TEXTFIELD}
                insertClickHandler={aiTextEnhancementUpdateHandler}
                enhancementFieldType={props.enhancementFieldType}
                registrationId={props.registrationId as string}
              />
            ) : null
          }
        />
        {formContextData.isReadOnly ? (
          getValueForReadOnlyFieldForStaticForm({
            renderType: FieldRenderType.MULTI_LINE_TEXTFIELD,
            value: field.value
          })
        ) : (
          <TextArea
            name={props.name}
            value={field.value}
            onBlur={field.onBlur}
            onChange={changeHandler}
            withCount={props.withCount}
            maxCount={props.maxCount}
            placeholder={props.placeholder}
            rows={props.rows}
            autoFocus={props.autoFocus}
            hasError={meta.touched && meta.error}
            disabled={props.disabled}
          />
        )}

        <ErrorField meta={meta} />
        {!isEmpty(props.conflictValue) && <FormOverwriteField forFieldName={props.name} disabled={props.disabled} />}
        {props.enableFieldReset && <FieldResetWhenRemoved name={props.name} />}
      </FormGroup>
    </React.Fragment>
  );
}

export default FormTextAreaField;
